import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { AuthUser, TenantBranding } from "@match/shared";
import { ApiError, api, tokens } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Login } from "@/pages/Login";
import { ChangePassword } from "@/pages/ChangePassword";
import { Home } from "@/pages/Home";
import { Accounts } from "@/pages/Accounts";
import { Clients } from "@/pages/Clients";
import { ClientDetail } from "@/pages/ClientDetail";
import { ClientLedgerPage } from "@/pages/ClientLedgerPage";
import { Trade } from "@/pages/Trade";
import { Sessions } from "@/pages/Sessions";
import { Cycles, CycleDetail } from "@/pages/Cycles";
import { SessionWizard } from "@/pages/SessionWizard";
import { SessionDetail } from "@/pages/SessionDetail";
import { PropFirms } from "@/pages/PropFirms";
import { Servers } from "@/pages/Servers";
import { Compare } from "@/pages/Compare";
import { Notifications } from "@/pages/Notifications";
import { Events } from "@/pages/Events";
import { Audit } from "@/pages/Audit";
import { MyHome, MyLedger } from "@/pages/Me";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // 4xx non si ritenta: un 403 o un 404 non guariscono da soli.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

/** Le variabili CSS scritte dall'ultimo marchio applicato, per poterle togliere. */
let applicate: string[] = [];

/**
 * Applica i token colore del tenant sovrascrivendo le variabili CSS.
 *
 * **Toglie PRIMA quelle di prima**, e non e' pulizia: da quando si entra tutti
 * dallo stesso indirizzo, la schermata di login e' condivisa. Un marchio che
 * resta addosso dopo l'uscita direbbe al prossimo che arriva da quel browser
 * di che gestore era chi c'era prima.
 */
function applyBranding(branding: TenantBranding | null) {
  const root = document.documentElement;
  for (const name of applicate) root.style.removeProperty(name);
  applicate = [];
  if (!branding?.tokens) return;
  for (const [name, value] of Object.entries(branding.tokens)) {
    const hex = value.dark ?? value.light;
    if (!hex) continue;
    const rgb = hexToRgb(hex);
    if (rgb) {
      root.style.setProperty(`--${name}`, rgb);
      applicate.push(`--${name}`);
    }
  }
}

function hexToRgb(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/**
 * L'icona del browser, dal marchio del tenant — dopo l'accesso, come il resto
 * del marchio: prima non c'e' un tenant da cui prenderla. `null` la toglie,
 * invece di lasciare quella dell'ultimo tenant uscito sullo schermo di login
 * condiviso.
 */
function applyFavicon(url: string | null | undefined) {
  const id = "tenant-favicon";
  const esistente = document.getElementById(id);
  if (!url) {
    esistente?.remove();
    return;
  }
  const link = (esistente as HTMLLinkElement | null) ?? document.createElement("link");
  link.id = id;
  link.rel = "icon";
  link.href = url;
  if (!esistente) document.head.appendChild(link);
}

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);

  /*
   * IL MARCHIO ARRIVA COL TOKEN, e prima dell'accesso non arriva affatto.
   *
   * Da quando il tenant si riconosce dal token e non dall'indirizzo, la
   * schermata di login e' la stessa per tutti: neutra, «Match Trading». Non e'
   * una rinuncia — un logo prima di sapere chi sta entrando non serve a
   * nessuno, e con un indirizzo solo non c'e' niente da cui dedurlo. Su un
   * dominio custom il backend lo risolve lo stesso, quindi li' si vede anche
   * prima.
   */
  const caricaMarchio = async () => {
    const b = await api.branding().catch(() => null);
    setBranding(b);
    applyBranding(b);
    applyFavicon(b?.faviconUrl);
    document.title = b?.name ?? "Match Trading";
  };

  useEffect(() => {
    void (async () => {
      await caricaMarchio();

      if (tokens.access) {
        const me = await api.me().catch(() => null);
        if (me) setUser(me);
        else tokens.clear();
      }
      setLoading(false);
    })();
  }, []);

  const tenantName = branding?.name ?? "Match Trading";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page text-sm text-muted">
        Caricamento…
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        onSignedIn={(u) => {
          setUser(u);
          // Adesso il tenant si sa: il marchio si chiede col token.
          void caricaMarchio();
        }}
        tenantName={tenantName}
      />
    );
  }

  /*
   * IL CAMBIO OBBLIGATO PRENDE IL POSTO DI TUTTO IL RESTO, come `Login`.
   *
   * Non un banner e non una voce di menu: finche' quella password e' quella che
   * gli abbiamo comunicato noi, **nessuna pagina del gestionale e' montata**.
   * Il backend rifiuta comunque (`lib/must-change-password.ts`): qui si evita di
   * mostrare porte chiuse, la' si tengono chiuse.
   */
  if (user.mustChangePassword) {
    return <ChangePassword user={user} tenantName={tenantName} onChanged={setUser} />;
  }

  const signOut = () => {
    // I dati in cache appartengono all'utente che esce. Il marchio anche.
    queryClient.clear();
    setUser(null);
    setBranding(null);
    applyBranding(null);
    applyFavicon(null);
    document.title = "Match Trading";
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout user={user} tenantName={tenantName} logoUrl={branding?.logoDarkUrl} onSignedOut={signOut} />}>
            {user.role === "CLIENT" ? (
              /*
               * L'AREA CLIENTE E' UNA TABELLA DI ROTTE SUA, non quella
               * dell'operatore con delle guardie sopra.
               *
               * E' la stessa forma dell'allowlist del backend, e per la stessa
               * ragione: **una rotta nuova nasce chiusa**. Montare le pagine
               * dell'operatore e sperare che ciascuna risponda 403 vuol dire
               * affidarsi a un controllo per pagina, cioe' alla cosa che
               * `lib/client-scope.ts` ha smesso di fare apposta. Qui il cliente
               * non puo' arrivare a una pagina nuova perche' non c'e', non
               * perche' qualcuno si e' ricordato di filtrarla.
               *
               * Il `*` in fondo riporta ai suoi conti: un indirizzo scritto a
               * mano non deve dare una pagina bianca.
               */
              <>
                <Route index element={<MyHome />} />
                <Route path="cicli" element={<Cycles />} />
                <Route path="cicli/:id" element={<CycleDetail />} />
                <Route path="movimenti" element={<MyLedger />} />
                <Route path="confronto" element={<Compare />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
            <Route index element={<Home />} />
            <Route path="conti" element={<Accounts />} />
            <Route path="clienti" element={<Clients />} />
            <Route path="clienti/:id" element={<ClientDetail />} />
            <Route path="clienti/:id/movimenti" element={<ClientLedgerPage />} />
            <Route path="server" element={<Servers />} />
            <Route path="prop-firm" element={<PropFirms />} />
            <Route path="operazione" element={<Trade />} />
            <Route path="cicli" element={<Cycles />} />
            <Route path="cicli/:id" element={<CycleDetail />} />
            <Route path="fasi" element={<Sessions />} />
            <Route path="fasi/nuova" element={<SessionWizard />} />
            <Route path="fasi/:id" element={<SessionDetail />} />
            <Route path="confronto" element={<Compare />} />
            <Route path="eventi" element={<Events />} />
            <Route path="audit" element={<Audit />} />
            <Route path="impostazioni/notifiche" element={<Notifications />} />
              </>
            )}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
